import React, { forwardRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { digitCount, isValidEmailFormat } from "../../lib/tracking";

const inputStyle = {
  width: "100%",
  height: 48,
  borderRadius: 8,
  backgroundColor: "#132236",
  color: "#FFFFFF",
  border: "1px solid #1A2E44",
  padding: "0 14px",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 14,
  outline: "none",
  transition: "border-color 150ms ease, box-shadow 150ms ease",
};

const LeadGate = forwardRef(function LeadGate(
  { onSubmit, loading },
  ref,
) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [budget, setBudget] = useState("");
  const [budgetFocused, setBudgetFocused] = useState(false);

  const normalizeWebsite = (raw) => {
    // Strip protocol and leading www., store clean domain only.
    return String(raw || "")
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .replace(/\/+$/, "");
  };

  const nameOk = name.trim().length > 0;
  const websiteClean = normalizeWebsite(website);
  const websiteOk = websiteClean.length > 0 && websiteClean.includes(".");
  const emailOk = isValidEmailFormat(email);
  const whatsappOk = digitCount(whatsapp) >= 10;
  const budgetOk = budget !== "";

  const canSubmit =
    nameOk && websiteOk && emailOk && whatsappOk && budgetOk && !loading;

  const normalizeWhatsapp = (raw) => {
    // Strip everything except digits, then normalize to E.164 with +91.
    let d = String(raw || "").replace(/\D+/g, "");
    if (d.startsWith("0")) d = d.slice(1);
    else if (d.startsWith("91")) d = d.slice(2);
    return `+91${d}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      website: websiteClean,
      email: email.trim(),
      whatsapp: normalizeWhatsapp(whatsapp),
      budget,
    });
  };

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "var(--rtd-navy)",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "96px 24px",
      }}
      data-testid="lead-gate-section"
    >
      <div className="grain-overlay" aria-hidden="true" />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 600,
          height: 600,
          background:
            "radial-gradient(circle, rgba(232,160,32,0.12) 0%, rgba(11,25,41,0) 60%)",
          pointerEvents: "none",
        }}
      />

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={handleSubmit}
        data-testid="lead-gate-form"
        className="relative w-full"
        style={{
          maxWidth: 480,
          backgroundColor: "#0F1E30",
          border: "1px solid #1A2E44",
          borderRadius: 16,
          padding: 28,
          boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
        }}
        noValidate
      >
        <h2
          className="font-serif-display"
          style={{ fontSize: 22, color: "#FFFFFF", lineHeight: 1.2, marginBottom: 8 }}
          data-testid="lead-gate-headline"
        >
          See your full diagnosis
        </h2>
        <p
          className="font-body"
          style={{
            fontSize: 14,
            color: "#C8DAE4",
            lineHeight: 1.55,
            marginBottom: 20,
          }}
        >
          Enter your details to see exactly what your score means and where your signal
          is breaking.
        </p>

        <label className="font-body" style={{ display: "block", marginBottom: 8 }}>
          <span
            style={{ display: "block", fontSize: 13, color: "#C8DAE4", marginBottom: 6, fontWeight: 500 }}
          >
            Your Name
          </span>
          <input
            data-testid="lead-gate-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name"
            style={{
              ...inputStyle,
              border: "1px solid #1A2E44",
            }}
            autoComplete="given-name"
          />
        </label>

        <label className="font-body" style={{ display: "block", marginBottom: 8 }}>
          <span
            style={{ display: "block", fontSize: 13, color: "#C8DAE4", marginBottom: 6, fontWeight: 500 }}
          >
            Clinic / Brand Website
          </span>
          <input
            data-testid="lead-gate-website"
            type="url"
            required
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="yourclinic.com"
            style={{
              ...inputStyle,
              border: "1px solid #1A2E44",
            }}
            autoComplete="url"
            inputMode="url"
          />
        </label>

        <label className="font-body" style={{ display: "block", marginBottom: 8 }}>
          <span
            style={{ display: "block", fontSize: 12, color: "#C8DAE4", marginBottom: 6, fontWeight: 500 }}
          >
            Business Email
          </span>
          <input
            data-testid="lead-gate-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={{
              ...inputStyle,
              border: "1px solid #1A2E44",
            }}
            autoComplete="email"
          />
        </label>

        <label className="font-body" style={{ display: "block", marginTop: 0, marginBottom: 0 }}>
          <span
            style={{ display: "block", fontSize: 12, color: "#C8DAE4", marginBottom: 6, fontWeight: 500 }}
          >
            WhatsApp Number
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              className="font-body"
              aria-hidden="true"
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: 14,
                color: "#FFFFFF",
                paddingRight: 8,
                flexShrink: 0,
              }}
            >
              +91
            </span>
            <input
              data-testid="lead-gate-whatsapp"
              type="tel"
              required
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="98765 43210"
              style={{
                ...inputStyle,
                border: "1px solid #1A2E44",
                flex: 1,
                minWidth: 0,
              }}
              autoComplete="tel"
            />
          </div>
        </label>

        <label className="font-body" style={{ display: "block", marginTop: 8, marginBottom: 16 }}>
          <span
            style={{ display: "block", fontSize: 13, color: "#C8DAE4", marginBottom: 6, fontWeight: 500 }}
          >
            Monthly ads budget
          </span>
          <select
            data-testid="lead-gate-budget"
            required
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            onFocus={() => setBudgetFocused(true)}
            onBlur={() => setBudgetFocused(false)}
            style={{
              ...inputStyle,
              border: `1px solid ${budgetFocused ? "#E8A020" : "#1A2E44"}`,
              color: budget === "" ? "#7A94A8" : "#FFFFFF",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%237A94A8' d='M6 8L0 0h12z'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 16px center",
              paddingRight: 40,
              cursor: "pointer",
            }}
          >
            <option value="" disabled hidden style={{ color: "#7A94A8" }}>
              Select your monthly ads budget
            </option>
            <option value="Under ₹1 Lakh" style={{ color: "#111827" }}>Under ₹1 Lakh</option>
            <option value="₹1 Lakh – ₹5 Lakh" style={{ color: "#111827" }}>₹1 Lakh – ₹5 Lakh</option>
            <option value="₹5 Lakh – ₹10 Lakh" style={{ color: "#111827" }}>₹5 Lakh – ₹10 Lakh</option>
            <option value="Above ₹10 Lakh" style={{ color: "#111827" }}>Above ₹10 Lakh</option>
            <option value="Prefer not to say" style={{ color: "#111827" }}>Prefer not to say</option>
          </select>
        </label>

        <button
          type="submit"
          data-testid="lead-gate-submit"
          disabled={!canSubmit}
          style={{
            width: "100%",
            height: 48,
            borderRadius: 8,
            border: "none",
            backgroundColor: "var(--rtd-gold)",
            color: "var(--rtd-navy)",
            fontFamily: "DM Sans, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            cursor: canSubmit ? "pointer" : "not-allowed",
            opacity: canSubmit ? 1 : 0.5,
            transition: "opacity 200ms ease, transform 150ms ease",
            boxShadow: canSubmit ? "0 10px 24px rgba(232,160,32,0.25)" : "none",
          }}
        >
          Show my full result →
        </button>

        <AnimatePresence>
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="lead-gate-loading"
              className="font-body"
              style={{
                marginTop: 12,
                fontSize: 13,
                color: "#7A94A8",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              Fetching your brand&rsquo;s signal profile…
            </motion.div>
          )}
        </AnimatePresence>

        <p
          className="font-body"
          style={{
            marginTop: 14,
            fontSize: 12,
            color: "#7A94A8",
            textAlign: "center",
          }}
        >
          No spam. We&rsquo;ll only reach out about your results.
        </p>
      </motion.form>
    </section>
  );
});

export default LeadGate;
